import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Airplane,
  Buildings,
  Sparkle,
  ArrowSquareOut,
  Star,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import { COUNTRIES, COUNTRY_BY_SLUG, CITIES_BY_COUNTRY } from "../lib/countries";
import { exploreOptions } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo, breadcrumbLd, touristDestinationLd } from "../components/Seo";
import { PartnerLogo } from "../components/PartnerLogo";
import { track } from "../lib/analytics";
import { toast, Toaster } from "sonner";

const COMPARE_OPTIONS = [
  {
    key: "flights",
    label: "Flights",
    icon: Airplane,
    blurb: "Real cached fares from Travelpayouts · 1-tap to Google Flights",
  },
  {
    key: "stays",
    label: "Hotels",
    icon: Buildings,
    blurb: "AI-curated rooms across Booking, Agoda, MakeMyTrip · in INR",
  },
  {
    key: "activities",
    label: "Activities",
    icon: Sparkle,
    blurb: "Compare ticket prices · Klook, GetYourGuide, Agoda",
    routeTo: "/activities",
  },
];

const ORIGINS = [
  "Delhi",
  "Mumbai",
  "Bengaluru",
  "Chennai",
  "Hyderabad",
  "Kolkata",
  "Ahmedabad",
  "Pune",
];

const ACCOMMODATION_TIERS = [
  { key: "budget", label: "Budget", hint: "Under ₹3,500/night" },
  { key: "balanced", label: "Balanced", hint: "₹3,500–8,000/night" },
  { key: "luxury", label: "Luxury", hint: "₹8,000–25,000/night" },
  { key: "beyond", label: "Beyond", hint: "₹25,000+/night" },
];

export default function Explore() {
  const { country: slugParam } = useParams();
  const navigate = useNavigate();
  const [slug, setSlug] = useState(slugParam || "thailand");
  const [category, setCategory] = useState("flights");
  const [origin, setOrigin] = useState("Delhi");
  const [destinationCity, setDestinationCity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [travelers, setTravelers] = useState(2);
  const [children, setChildren] = useState(0);
  const [accomTier, setAccomTier] = useState("balanced");
  const [budget, setBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const country = COUNTRY_BY_SLUG[slug];
  const citiesForCountry = CITIES_BY_COUNTRY[slug] || [];

  useEffect(() => {
    if (slugParam && slugParam !== slug) setSlug(slugParam);
  }, [slugParam]);

  // When user picks the "Activities" pill at the top, redirect to /activities.
  const onCompareChange = (key) => {
    const opt = COMPARE_OPTIONS.find((o) => o.key === key);
    if (opt?.routeTo) {
      navigate(opt.routeTo);
      return;
    }
    setCategory(key);
    setData(null);
  };

  const handleSearch = async () => {
    if (category === "flights") {
      if (!startDate) {
        toast.error("Pick a departure date first");
        return;
      }
    } else if (category === "stays") {
      if (!startDate || !endDate) {
        toast.error("Pick check-in and check-out dates");
        return;
      }
    }
    track("explore_search", {
      country: slug,
      category,
      origin,
      destination_city: destinationCity || null,
      start_date: startDate,
      end_date: endDate,
      travelers: Number(travelers),
      children: Number(children),
      accommodation_tier: category === "stays" ? accomTier : null,
      budget: budget ? Number(budget) : null,
    });
    setLoading(true);
    setData(null);
    try {
      const res = await exploreOptions({
        country_slug: slug,
        category,
        origin_city: origin,
        destination_city: destinationCity || undefined,
        start_date: startDate,
        end_date: endDate || undefined,
        travelers: Number(travelers),
        children: Number(children),
        accommodation_tier: category === "stays" ? accomTier : undefined,
        max_budget_inr: budget ? Number(budget) : undefined,
      });
      setData(res);
      track("explore_results", {
        country: slug,
        category,
        count: res.options?.length || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch results");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-mobile-nav" data-testid="explore-page">
      <Seo
        title={
          country
            ? `${country.name} budget flights, stays & activities from India`
            : "Compare prices · Flycher"
        }
        description={
          country
            ? `Compare AI-curated flights, stays, and things to do in ${country.name} for Indian travelers. Real flight fares from Travelpayouts, stays & activities deep-linked to Booking, Agoda, Klook and more — priced in INR.`
            : "Real flight fares + AI-curated stays, activities & food picks — deep-linked to Google Flights, Booking, Agoda and more in one tap."
        }
        path={slugParam ? `/explore/${slugParam}` : "/explore"}
        image={country?.image}
        jsonLd={
          country
            ? [
                breadcrumbLd([
                  { name: "Home", path: "/" },
                  { name: "Compare Prices", path: "/explore" },
                  { name: country.name, path: `/explore/${country.slug}` },
                ]),
                touristDestinationLd({
                  name: country.name,
                  description: country.tagline,
                  image: country.image,
                  path: `/explore/${country.slug}`,
                }),
              ]
            : breadcrumbLd([
                { name: "Home", path: "/" },
                { name: "Compare Prices", path: "/explore" },
              ])
        }
      />
      <Toaster theme="light" position="top-center" offset={80} />
      <Navbar />
      <div className="pt-24 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-6 sm:mb-8">
          <span className="kicker">— Compare prices</span>
          <h1 className="font-serif text-3xl sm:text-6xl mt-3 text-slate-900">
            Compare. Filter.{" "}
            <span className="italic" style={{ color: "#2A7FB8" }}>
              Book smart.
            </span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-xl text-sm leading-relaxed">
            AI-curated picks across Google Flights, Booking, Agoda, MakeMyTrip & more —
            deep-linked to the live booking page in one tap.
          </p>
        </div>

        {/* Search bar */}
        <form
          data-testid="explore-search"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          className="card-surface rounded-2xl p-5 grid grid-cols-1 md:grid-cols-12 gap-4"
        >
          <div className="md:col-span-12">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">
              What do you want to compare?
            </p>
            <div
              className="flex gap-2 -mx-1 px-1 overflow-x-auto flex-nowrap sm:flex-wrap w-full scrollbar-hide"
              data-testid="compare-tabs"
            >
              {COMPARE_OPTIONS.map((c) => {
                const active = category === c.key && !c.routeTo;
                const Icon = c.icon;
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`tab-${c.key}`}
                    onClick={() => onCompareChange(c.key)}
                    className={`group relative inline-flex items-center gap-2.5 px-4 sm:px-5 py-3 rounded-2xl text-sm whitespace-nowrap flex-shrink-0 transition border ${
                      active
                        ? "text-white shadow-md shadow-sky-200/70 border-transparent"
                        : "text-slate-700 border-slate-200 bg-white hover:border-[#2A7FB8]/40"
                    }`}
                    style={active ? { background: "#2A7FB8" } : {}}
                  >
                    <Icon size={18} weight="duotone" />
                    <span className="font-semibold">{c.label}</span>
                    {c.routeTo && (
                      <ArrowRight
                        size={12}
                        weight="bold"
                        className="ml-0.5 opacity-60"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* FLIGHTS form */}
          {category === "flights" && (
            <>
              <Field label="To country" className="md:col-span-3">
                <select
                  data-testid="input-country"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="select-base"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="From" className="md:col-span-3">
                <select
                  data-testid="input-origin"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className="select-base"
                >
                  {ORIGINS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="When are you departing?" className="md:col-span-2">
                <input
                  type="date"
                  data-testid="input-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Travellers" className="md:col-span-2">
                <input
                  type="number"
                  min={1}
                  data-testid="input-travelers"
                  value={travelers}
                  onChange={(e) => setTravelers(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Max budget ₹ (pp)" className="md:col-span-2">
                <input
                  type="number"
                  data-testid="input-budget"
                  value={budget}
                  placeholder="50000"
                  onChange={(e) => setBudget(e.target.value)}
                  className="input-base"
                />
              </Field>
            </>
          )}

          {/* HOTELS form */}
          {category === "stays" && (
            <>
              <Field label="Country" className="md:col-span-3">
                <select
                  data-testid="input-country"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setDestinationCity("");
                  }}
                  className="select-base"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="City" className="md:col-span-3">
                {citiesForCountry.length > 0 ? (
                  <select
                    data-testid="input-destination-city"
                    value={destinationCity}
                    onChange={(e) => setDestinationCity(e.target.value)}
                    className="select-base"
                  >
                    <option value="">Any city in {country?.name}</option>
                    {citiesForCountry.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid="input-destination-city"
                    value={destinationCity}
                    onChange={(e) => setDestinationCity(e.target.value)}
                    placeholder="e.g. Bangkok"
                    className="input-base"
                  />
                )}
              </Field>
              <Field label="Check-in" className="md:col-span-2">
                <input
                  type="date"
                  data-testid="input-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Check-out" className="md:col-span-2">
                <input
                  type="date"
                  data-testid="input-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Adults" className="md:col-span-1">
                <input
                  type="number"
                  min={1}
                  data-testid="input-travelers"
                  value={travelers}
                  onChange={(e) => setTravelers(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Children" className="md:col-span-1">
                <input
                  type="number"
                  min={0}
                  data-testid="input-children"
                  value={children}
                  onChange={(e) => setChildren(e.target.value)}
                  className="input-base"
                />
              </Field>
              <div className="md:col-span-12">
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">
                  What kind of hotel?
                </p>
                <div
                  className="flex gap-2 -mx-1 px-1 overflow-x-auto flex-nowrap sm:flex-wrap w-full scrollbar-hide"
                  data-testid="tier-tabs"
                >
                  {ACCOMMODATION_TIERS.map((t) => {
                    const active = accomTier === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`tier-${t.key}`}
                        onClick={() => setAccomTier(t.key)}
                        className={`flex flex-col items-start gap-0.5 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap flex-shrink-0 transition border ${
                          active
                            ? "text-white shadow-md shadow-sky-200/70 border-transparent"
                            : "text-slate-700 border-slate-200 bg-white hover:border-[#2A7FB8]/40"
                        }`}
                        style={active ? { background: "#2A7FB8" } : {}}
                      >
                        <span className="font-semibold">{t.label}</span>
                        <span
                          className={`text-[10px] font-mono ${
                            active ? "text-white/80" : "text-slate-400"
                          }`}
                        >
                          {t.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="md:col-span-12 flex flex-wrap items-center gap-3 pt-2 justify-end">
            <button
              type="submit"
              data-testid="btn-search"
              onClick={handleSearch}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-50 shadow-lg shadow-orange-200/70"
              style={{ background: "#FF7E4D", color: "#ffffff" }}
            >
              {loading
                ? "Searching…"
                : category === "flights"
                ? "Find best flights"
                : "Find best hotels"}
            </button>
          </div>
        </form>

        {/* Results */}
        <div className="mt-10 pb-24">
          {loading && (
            <div
              data-testid="explore-loading"
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="card-surface rounded-2xl p-6 h-44 animate-pulse"
                />
              ))}
            </div>
          )}

          {data && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl sm:text-3xl">
                  {data.options.length} curated picks in {data.country}
                </h2>
                <Link
                  to={`/experts/${slug}`}
                  className="text-xs text-[#2A7FB8] underline-offset-4 hover:underline"
                >
                  Ask the {country?.name} expert →
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.options.map((o, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="card-surface rounded-2xl p-5"
                    data-testid={`result-card-${idx}`}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <div className="font-semibold text-base text-slate-900">
                          {o.title}
                        </div>
                        {o.subtitle && (
                          <div className="text-xs text-slate-500 mt-1">
                            {o.subtitle}
                          </div>
                        )}
                      </div>
                      {o.rating && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#F4B942] font-semibold">
                          <Star size={12} weight="fill" /> {o.rating}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                      {o.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {o.badges?.map((b) => (
                        <span
                          key={b}
                          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-slate-200 bg-[#EAF4FB] text-slate-700"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <span
                        className="font-mono text-sm font-semibold"
                        style={{ color: "#2A7FB8" }}
                      >
                        {o.price_inr}
                      </span>
                      {o.deeplinks?.length === 1 ? (
                        <a
                          href={o.deeplinks[0].url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          referrerPolicy="no-referrer"
                          data-testid={`deeplink-${o.deeplinks[0].name.toLowerCase().replace(/\s/g, "-")}-${idx}`}
                          onClick={() =>
                            track("explore_deeplink_clicked", {
                              country: slug,
                              category,
                              partner: o.deeplinks[0].name,
                              option_title: o.title,
                            })
                          }
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3.5 py-2 rounded-full shadow-sm"
                          style={{ background: "#FF7E4D" }}
                        >
                          Open on {o.deeplinks[0].name}
                          <ArrowSquareOut size={12} weight="bold" />
                        </a>
                      ) : (
                        <details className="group">
                          <summary
                            data-testid={`book-toggle-${idx}`}
                            className="cursor-pointer list-none inline-flex items-center gap-1 text-xs text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#2A7FB8]/50"
                          >
                            Book on platforms <CaretDown size={12} />
                          </summary>
                          <div className="absolute mt-2 z-10 rounded-xl border border-slate-200 bg-white shadow-xl p-2 min-w-[180px]">
                            {o.deeplinks?.map((d) => (
                              <a
                                key={d.name}
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`deeplink-${d.name.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() =>
                                  track("explore_deeplink_clicked", {
                                    country: slug,
                                    category,
                                    partner: d.name,
                                    option_title: o.title,
                                  })
                                }
                                className="flex items-center justify-between gap-3 px-3 py-2 text-xs text-slate-700 hover:text-[#2A7FB8]"
                              >
                                <PartnerLogo name={d.name} size={16} />
                                <ArrowSquareOut size={12} />
                              </a>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {data.tips?.length > 0 && (
                <div className="mt-10 card-surface rounded-2xl p-6">
                  <span className="kicker">— Insider tips</span>
                  <ul className="mt-3 space-y-2 text-sm text-slate-700">
                    {data.tips.map((t, i) => (
                      <li key={i} className="flex gap-3">
                        <span style={{ color: "#FF7E4D" }}>—</span> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!data && !loading && (
            <div
              data-testid="explore-empty"
              className="card-surface rounded-2xl p-12 text-center"
            >
              <span className="kicker">Get started</span>
              <p className="font-serif text-3xl mt-3 text-slate-900">
                Pick a country, drop your dates,
                <br />
                let the AI find your best price.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1.5 md:col-span-2 ${className}`}>
    <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
      {label}
    </span>
    {children}
  </label>
);
